// ExportMenu — Export ▾ dropdown (XLSX/CSV/PDF) — ústava sek. 66.5
// Použitie:
//   <ExportMenu
//     data={filtered}
//     columns={[{ key: 'id', label: 'ID' }, { key: 'name', label: 'Názov' }, ...]}
//     filename="zakazky-2026"
//   />
import React, { useState, useRef, useEffect } from 'react'
import { fetchAuth } from '../auth/fetchAuth.js'
import { COLORS, TYPO, SPACE, RADIUS } from '../theme.js'
import { useToast } from './Toast.jsx'


let _logoUrl = null
let _logoPromise = null
function loadCompanyLogo() {
  if (_logoUrl !== null) return Promise.resolve(_logoUrl)
  if (_logoPromise) return _logoPromise
  _logoPromise = fetchAuth('/api/company-settings')
    .then(r => r.ok ? r.json() : [])
    .then(rows => {
      const logo = (rows || []).find(x => x.key === 'logo')
      _logoUrl = logo && logo.value ? '/uploads/' + logo.value : ''
      return _logoUrl
    })
    .catch(() => { _logoUrl = ''; return '' })
  return _logoPromise
}

function escapeCsv(v) {
  if (v == null) return ''
  const s = String(v)
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return '"' + s.replace(/"/g, '""') + '"'
  }
  return s
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export default function ExportMenu({ data = [], columns = [], filename = 'export', title = 'Export' }) {
  const [open, setOpen] = useState(false)
  const btnRef = useRef(null)
  const menuRef = useRef(null)
  const toast = useToast()

  useEffect(() => {
    if (!open) return
    function onClick(e) {
      if (menuRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  function exportCsv() {
    setOpen(false)
    if (!data.length) { toast.error('Žiadne dáta na export'); return }
    const headers = columns.map(c => escapeCsv(c.label || c.key)).join(',')
    const rows = data.map(r => columns.map(c => {
      const v = typeof c.value === 'function' ? c.value(r) : r[c.key]
      return escapeCsv(v)
    }).join(','))
    const csv = '\uFEFF' + [headers, ...rows].join('\n')  // BOM pre Excel UTF-8
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    downloadBlob(blob, `${filename}.csv`)
    toast.success(`Exportované ${data.length} riadkov (CSV)`)
  }

  function exportXlsx() {
    setOpen(false)
    if (!data.length) { toast.error('Žiadne dáta na export'); return }
    // XLSX cez SpreadsheetML 2003 — jednoduchý XML formát, otvorí ho Excel/Numbers/LibreOffice
    const escape = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    const headerRow = columns.map(c => `<Cell><Data ss:Type="String">${escape(c.label || c.key)}</Data></Cell>`).join('')
    const dataRows = data.map(r => {
      const cells = columns.map(c => {
        const v = typeof c.value === 'function' ? c.value(r) : r[c.key]
        const isNum = typeof v === 'number'
        return `<Cell><Data ss:Type="${isNum ? 'Number' : 'String'}">${escape(v)}</Data></Cell>`
      }).join('')
      return `<Row>${cells}</Row>`
    }).join('')
    const xml = `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
<Worksheet ss:Name="${escape(title)}"><Table>
<Row>${headerRow}</Row>${dataRows}
</Table></Worksheet></Workbook>`
    const blob = new Blob([xml], { type: 'application/vnd.ms-excel' })
    downloadBlob(blob, `${filename}.xls`)
    toast.success(`Exportované ${data.length} riadkov (XLS)`)
  }

  async function exportPdf() {
    const logoUrl = await loadCompanyLogo()
    setOpen(false)
    if (!data.length) { toast.error('Žiadne dáta na export'); return }
    // PDF A4 — strana po strane, browser print → uloží ako PDF
    // Vlastné HTML s @page CSS + opakované thead na každej strane
    const win = window.open('', '_blank', 'width=900,height=1000')
    if (!win) { toast.error('Otvor pop-up okná'); return }
    const headRow = columns.map(c => `<th>${escape(c.label || c.key)}</th>`).join('')
    const bodyRows = data.map(r => {
      const tds = columns.map(c => {
        const v = typeof c.value === 'function' ? c.value(r) : r[c.key]
        return `<td>${escape(v == null ? '' : String(v))}</td>`
      }).join('')
      return `<tr>${tds}</tr>`
    }).join('')
    function escape(s) {
      return String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
    }
    const today = new Date().toLocaleString('sk-SK', { dateStyle: 'long', timeStyle: 'short' })
    const html = `<!doctype html>
<html lang="sk"><head><meta charset="utf-8"><title>${escape(title)} — KAMIR</title>
<style>
  @page {
    size: A4 portrait;
    margin: 1.6cm 1.2cm 1.8cm;
    @bottom-right {
      content: "Strana " counter(page) " / " counter(pages);
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 9pt; color: #888;
    }
    @bottom-left {
      content: "KAMIR s.r.o. · IČO 36447749 · vytlačené " "${escape(today)}";
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 8pt; color: #999;
    }
  }
  body {
    font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
    color: #111; margin: 0; padding: 0;
    font-size: 9.5pt;
  }
  .doc-header {
    display: flex; align-items: flex-end; gap: 16px;
    border-bottom: 3px solid #ef9350;
    padding-bottom: 10px; margin-bottom: 16px;
  }
  .doc-header h1 {
    margin: 0; font-size: 16pt; color: #111;
    font-weight: 700;
  }
  .doc-header .brand {
    font-size: 18pt; font-weight: 800; color: #ef9350; letter-spacing: -0.02em;
  }
  .doc-meta {
    margin-left: auto; text-align: right;
    font-size: 8.5pt; color: #666;
  }
  table {
    width: 100%; border-collapse: collapse;
    font-size: 9pt;
  }
  thead { display: table-header-group; }   /* OPAKUJE header na každej strane */
  tfoot { display: table-footer-group; }
  th {
    background: #fef5ee; color: #92400e;
    font-weight: 700; text-transform: uppercase;
    font-size: 8pt; letter-spacing: 0.04em;
    padding: 6px 8px; text-align: left;
    border-bottom: 2px solid #ef9350;
  }
  td {
    padding: 5px 8px; vertical-align: top;
    border-bottom: 1px solid #e5e5e5;
  }
  tbody tr { page-break-inside: avoid; }
  tbody tr:nth-child(even) { background: #fafafa; }
  .summary {
    margin-top: 14px; padding: 8px 10px;
    background: #fef5ee; border: 1px solid #ef9350;
    font-size: 9pt; border-radius: 3px;
  }
  .toolbar {
    position: fixed; top: 10px; right: 10px;
    background: #fff; border: 1px solid #ddd; padding: 8px;
    border-radius: 6px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    z-index: 100;
  }
  .toolbar button {
    margin-right: 6px; padding: 6px 14px;
    background: #ef9350; color: #fff; border: none;
    border-radius: 4px; cursor: pointer; font-size: 12px;
    font-family: inherit;
  }
  .toolbar .ghost {
    background: #fff; color: #666; border: 1px solid #ddd;
  }
  @media print {
    .toolbar { display: none; }
  }
</style></head><body>
<div class="toolbar">
  <button onclick="window.print()">Tlačiť / Uložiť ako PDF</button>
  <button class="ghost" onclick="window.close()">Zavrieť</button>
</div>
<div class="doc-header">
  <span class="brand">M</span>
  <h1>${escape(title)}</h1>
  <div class="doc-meta">
    <div><strong>${data.length} riadkov</strong></div>
    <div>${escape(today)}</div>
  </div>
</div>
<table>
  <thead><tr>${headRow}</tr></thead>
  <tbody>${bodyRows}</tbody>
</table>
<div class="summary">
  <strong>Spolu:</strong> ${data.length} záznamov · KAMIR s.r.o.
</div>
</body></html>`
    win.document.write(html)
    win.document.close()
    setTimeout(() => win.print(), 600)
    toast.success(`A4 tlač otvorená (${data.length} riadkov)`)
  }

  const opts = [
    { key: 'xlsx', label: 'Excel (XLS)', onClick: exportXlsx },
    { key: 'csv',  label: 'CSV',         onClick: exportCsv  },
    { key: 'pdf',  label: 'PDF / tlač',   onClick: exportPdf  },
  ]

  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={btnRef}
        onClick={() => setOpen(o => !o)}
        style={{
          padding: '6px 12px',
          background: COLORS.bg,
          border: `1px solid ${COLORS.line}`,
          borderRadius: RADIUS.sm,
          cursor: 'pointer',
          fontSize: TYPO.sizes.sm,
          color: COLORS.text,
          fontFamily: 'inherit',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = COLORS.bgSoft}
        onMouseLeave={(e) => e.currentTarget.style.background = COLORS.bg}
      >Export <span style={{ fontSize: 10 }}>▾</span></button>
      {open && (
        <div
          ref={menuRef}
          style={{
            position: 'absolute', top: '100%', right: 0, marginTop: 4,
            background: COLORS.bg, border: `1px solid ${COLORS.line}`,
            borderRadius: RADIUS.md, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            zIndex: 1000, minWidth: 180, padding: '4px 0',
          }}
        >
          {opts.map(o => (
            <button key={o.key} onClick={o.onClick}
              style={{
                display: 'block', width: '100%', padding: '8px 14px',
                background: 'transparent', border: 'none', textAlign: 'left',
                fontSize: TYPO.sizes.sm, cursor: 'pointer', fontFamily: 'inherit',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = COLORS.bgSoft}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >{o.label}</button>
          ))}
        </div>
      )}
    </span>
  )
}
